/** Presentational building blocks. Each returns Safe markup via html``; all text is auto-escaped. */
import { esc, html, raw, safeUrl } from '../core/html.js';
import { hue, initials } from '../core/format.js';
import { ICONS } from './icons.js';

export function ic(name, cls = '') {
  return html`<svg class="ico ${cls}" viewBox="0 0 24 24" aria-hidden="true">${raw(ICONS[name] || '')}</svg>`;
}

export function logoMark() {
  return html`<span class="mark"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 6l7 12 7-12"/></svg></span>`;
}

export const tag = (text, cls = '') => html`<span class="tag ${cls}">${text}</span>`;

export function chips(items, cls = 'n') {
  const list = Array.isArray(items) ? items : String(items ?? '').split(',');
  return html`<div class="chips">${list.map((x) => String(x).trim()).filter(Boolean).map((x) => tag(x, cls))}</div>`;
}

export function bar(percent, cls = '', label = 'Progress') {
  const p = Math.max(0, Math.min(100, Math.round(Number(percent) || 0)));
  return html`<div class="bar ${cls}" role="progressbar" aria-label="${label}" aria-valuenow="${p}" aria-valuemin="0" aria-valuemax="100"><i style="width:${p}%"></i></div>`;
}

export function ring(percent) {
  const p = Math.max(0, Math.min(100, Math.round(Number(percent) || 0)));
  return html`<div class="ring" style="--p:${p}" role="img" aria-label="${p} percent"><b>${p}%</b></div>`;
}

export function stat(value, label, delta = '') {
  return html`<div class="card stat"><div class="l">${label}</div><div class="k">${value}</div>${delta ? html`<div class="d">${delta}</div>` : ''}</div>`;
}

export const grid = (items, cls = '') => html`<div class="grid ${cls}">${items}</div>`;

export function table(head, rows, { id = '', empty: emptyText = 'Nothing here yet.', caption = '' } = {}) {
  return html`<div class="tw"><table ${id ? raw(`id="${esc(id)}"`) : ''}>${caption ? html`<caption class="sr-only">${caption}</caption>` : ''}<thead><tr>${head.map((h) => html`<th scope="col">${h}</th>`)}</tr></thead><tbody>${
    rows.length ? rows.map((r) => html`<tr>${r.map((c) => html`<td>${c}</td>`)}</tr>`) : html`<tr><td colspan="${head.length}" class="mu">${emptyText}</td></tr>`
  }</tbody></table></div>`;
}

/** Avatar: photo when we have one, otherwise a coloured monogram. */
export function av(name, cls = '', photoUrl = '') {
  if (photoUrl) return html`<img class="av ${cls}" src="${safeUrl(photoUrl)}" alt="${name}" loading="lazy">`;
  return html`<span class="av ${cls}" style="--h:${hue(name)}" aria-hidden="true">${initials(name)}</span>`;
}

export function who(name, sub = '') {
  return html`<div class="who">${av(name, 'sm')}<div><div class="b">${name}</div>${sub ? html`<div class="xs mu">${sub}</div>` : ''}</div></div>`;
}

export function empty(icon, title, text, action = '') {
  return html`<div class="card empty">${ic(icon)}<h3>${title}</h3><p>${text}</p>${action}</div>`;
}

export function banner(content, kind = '', icon = 'info') {
  return html`<div class="banner ${kind}" role="${kind === 'bad' ? 'alert' : 'status'}">${ic(icon)}<div>${content}</div></div>`;
}

/** Segmented control. `act` is the action name; the option value goes in data-value. */
export function seg(options, current, act, label = 'Options') {
  return html`<div class="seg" role="tablist" aria-label="${label}">${options.map((o) => {
    const [value, text] = Array.isArray(o) ? o : [o, o];
    return html`<button type="button" role="tab" aria-selected="${value === current}" class="${value === current ? 'on' : ''}" data-act="${act}" data-value="${value}">${text}</button>`;
  })}</div>`;
}

export function pageHead(title, sub = '', right = '') {
  return html`<div class="page-h"><div><h2 style="margin:0 0 4px">${title}</h2>${sub ? html`<p>${sub}</p>` : ''}</div>${right ? html`<div class="row">${right}</div>` : ''}</div>`;
}

export function backLink(to, label = 'Back') {
  return html`<a class="back" href="${to}">${ic('left')} ${label}</a>`;
}

export function spinner(size = 16) {
  return html`<span class="load" style="width:${size}px;height:${size}px" role="status" aria-label="Loading"></span>`;
}

export function loadingBlock(text = 'Loading') {
  return html`<div class="card empty" role="status" aria-live="polite">${spinner(26)}<p style="margin-top:12px">${text}</p></div>`;
}

export function errorBlock(error, retry = true) {
  const message = error?.message || 'Something went wrong.';
  return html`<div class="card empty" role="alert">${ic('alert')}<h3>We could not load this</h3><p>${message}</p>${retry ? html`<button class="btn" data-act="app.retry">Try again</button>` : ''}</div>`;
}

const STATUS = {
  // generic
  active: ['Active', 'ok'], paid: ['Paid', 'ok'], verified: ['Verified', 'ok'], pending: ['Pending', 'wn'], failed: ['Failed', 'bad'],
  created: ['Awaiting payment', 'wn'], open: ['Open', ''], closed: ['Closed', 'n'], draft: ['Draft', 'n'], suspended: ['Suspended', 'bad'],
  unverified: ['Not verified', 'wn'], rejected: ['Rejected', 'bad'], deleted: ['Deleted', 'n'],
  // requirements and hiring
  in_review: ['In review', 'wn'], matched: ['Matched', 'ok'], Shortlisted: ['Shortlisted', ''], Interview: ['Interview', 'wn'], Offer: ['Offer', 'wn'],
  Hired: ['Hired', 'ok'], Rejected: ['Rejected', 'bad'], applied: ['Applied', 'wn'], shortlisted: ['Shortlisted', 'ok'], withdrawn: ['Withdrawn', 'n'],
  sent: ['Sent', 'wn'], recommended: ['Recommended', 'ok'], declined: ['Declined', 'n'], accepted: ['Accepted', 'ok'],
  // projects
  starting: ['Starting', ''], in_progress: ['In progress', 'wn'], completed: ['Completed', 'ok'], cancelled: ['Cancelled', 'n'],
  submitted: ['In review', 'wn'], approved: ['Approved', 'ok'], changes: ['Changes asked', 'wn'],
  // classes and recordings
  scheduled: ['Scheduled', 'n'], live: ['Live', 'bad'], ended: ['Ended', 'n'], processing: ['Processing', 'wn'], ready: ['Ready', 'ok'],
  // money
  requested: ['Requested', 'wn'], refunded: ['Refunded', 'ok'], on_hold: ['On hold', 'bad'], kept: ['Kept', 'n'], flagged: ['Flagged', 'bad'],
  expired: ['Expired', 'n'], resolved: ['Resolved', 'ok'], forwarded: ['Forwarded', 'wn'], offered: ['Offered', 'wn'], running: ['Running', 'ok'],
};

export function statusTag(status) {
  const [label, cls] = STATUS[status] || [String(status ?? '').replace(/_/g, ' '), 'n'];
  return tag(label, cls);
}

/** Label/value rows used in receipts and details. */
export function kv(rows) {
  return html`<table>${rows.filter(Boolean).map(([k, v]) => html`<tr><td class="mu">${k}</td><td>${v}</td></tr>`)}</table>`;
}
